import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { clearToken, isAppAdmin, login as apiLogin, me, setToken } from '../lib/authApi';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setError(null);
    const out = await me();
    if (out.user) {
      setUser(out.user);
      return out.user;
    }
    setUser(null);
    return null;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await refresh();
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [refresh]);

  const login = useCallback(async (identifier, password) => {
    setError(null);
    const out = await apiLogin(identifier, password);
    if (!out.success || !out.token) {
      setError(out.error || 'Login failed');
      return { success: false, error: out.error };
    }
    setToken(out.token);
    const u = out.user || (await refresh());
    if (!isAppAdmin(u)) {
      // Credentials were valid but this is not an OnMyWay admin account
      // (e.g. an activity organizer / merchant). Reject from the admin
      // portal and let the UI redirect them to the organizer portal.
      clearToken();
      setUser(null);
      const msg = '此账户不是管理员账户';
      setError(msg);
      return { success: false, error: msg, notAdmin: true };
    }
    // Commit the authenticated user to context so ProtectedRoute lets us in.
    // Without this, when /api/auth/login already returns `user` we skip
    // refresh() (the only other setUser caller) and the guard bounces us back
    // to /admin/login even though the token was stored.
    setUser(u);
    return { success: true, user: u };
  }, [refresh]);

  const logout = useCallback(() => {
    clearToken();
    setUser(null);
    setError(null);
  }, []);

  const value = useMemo(() => ({
    user,
    loading,
    error,
    isAdmin: isAppAdmin(user),
    login,
    logout,
    refresh,
  }), [user, loading, error, login, logout, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
